/**
 * StoreLanding — la radice del sito sul web (#74).
 *
 * Puzzly si usa dall'app: sul web restano l'apertura dei puzzle ricevuti
 * (/app#<id>) e le pagine legali. Qui si dice dove scaricarla. I due indirizzi
 * arrivano dalla build (REACT_APP_APP_STORE_URL, REACT_APP_PLAY_STORE_URL):
 * finché uno manca quel pulsante non c'è, e se mancano tutti e due si dice
 * che l'app sta arrivando. Niente link verso schede che non esistono ancora.
 *
 * Un link di accesso via email aperto qui (?magic=1, o i parametri di Firebase)
 * sul web non porta da nessuna parte: va aperto sul telefono con l'app.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";

export default function StoreLanding() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const parametri = new URLSearchParams(search);
  const linkDiAccesso = parametri.has("magic") || parametri.get("mode") === "signIn";
  const appStore = process.env.REACT_APP_APP_STORE_URL;
  const playStore = process.env.REACT_APP_PLAY_STORE_URL;
  const store = [
    appStore && { href: appStore, label: t("store.appStore"), id: "store-apple" },
    playStore && { href: playStore, label: t("store.playStore"), id: "store-google" },
  ].filter(Boolean);

  return (
    <main style={pagina}>
      <SEO
        title={t("store.seoTitle")}
        description={t("store.seoDescription")}
        canonical="https://puzzly.giadalabs.com/"
      />
      <div style={colonna}>
        <div style={marchio}>
          <span style={logo} aria-hidden="true">P</span>
          <span style={nome}>Puzzly</span>
        </div>

        <h1 style={titolo}>{t("store.title")}</h1>
        <p style={testo}>{t("store.body")}</p>

        {linkDiAccesso && (
          <p style={avviso} role="status" data-testid="store-magic-link">
            {t("store.magicLink")}
          </p>
        )}

        {store.length > 0 ? (
          <div style={pulsanti}>
            {store.map((s) => (
              <a key={s.id} href={s.href} style={pulsante} data-testid={s.id}>
                {s.label}
              </a>
            ))}
          </div>
        ) : (
          <p style={presto} data-testid="store-soon">{t("store.soon")}</p>
        )}

        <p style={ricevuto}>{t("store.received")}</p>
      </div>

      <footer style={piede}>
        <Link to="/privacy" style={voce}>{t("store.privacy")}</Link>
        <Link to="/terms" style={voce}>{t("store.terms")}</Link>
        <Link to="/cookies" style={voce}>{t("store.cookies")}</Link>
        <a href="mailto:hello@giadalabs.com" style={voce}>hello@giadalabs.com</a>
      </footer>
    </main>
  );
}

const pagina = {
  minHeight: "100vh",
  background: "#fcf5f7",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "calc(env(safe-area-inset-top, 0px) + 48px) 24px calc(env(safe-area-inset-bottom, 0px) + 24px)",
  boxSizing: "border-box",
};
const colonna = { maxWidth: 440, width: "100%", margin: "0 auto" };
const marchio = { display: "flex", alignItems: "center", gap: 10, marginBottom: 40 };
const logo = {
  width: 40,
  height: 40,
  borderRadius: 12,
  background: "linear-gradient(135deg, #a8275a, #ff709f)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 800,
  fontSize: 20,
  boxShadow: "0 8px 24px rgba(168,39,90,0.25)",
};
const nome = { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 22, color: "#3b0f24" };
const titolo = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 800,
  fontSize: 36,
  lineHeight: 1.1,
  letterSpacing: "-0.02em",
  color: "#3b0f24",
  margin: "0 0 16px",
};
const testo = {
  fontFamily: "'Be Vietnam Pro', sans-serif",
  fontSize: 17,
  lineHeight: 1.55,
  color: "#6b4a58",
  margin: "0 0 28px",
};
const avviso = {
  fontFamily: "'Be Vietnam Pro', sans-serif",
  fontSize: 15,
  lineHeight: 1.5,
  color: "#3b0f24",
  background: "#f5e3ea",
  borderRadius: 16,
  padding: "14px 16px",
  margin: "0 0 24px",
};
const pulsanti = { display: "grid", gap: 12, marginBottom: 28 };
const pulsante = {
  display: "block",
  textAlign: "center",
  padding: "16px 20px",
  borderRadius: 16,
  background: "linear-gradient(135deg, #a8275a, #ff709f)",
  color: "#fff",
  textDecoration: "none",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 700,
  fontSize: 17,
  boxShadow: "0 8px 24px rgba(168,39,90,0.25)",
};
const presto = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 700,
  fontSize: 17,
  color: "#a8275a",
  background: "#fff",
  borderRadius: 16,
  padding: "16px 20px",
  textAlign: "center",
  margin: "0 0 28px",
  boxShadow: "0 8px 24px rgba(59,15,36,0.06)",
};
const ricevuto = {
  fontFamily: "'Be Vietnam Pro', sans-serif",
  fontSize: 14,
  lineHeight: 1.5,
  color: "#8a6a78",
  margin: 0,
};
const piede = {
  maxWidth: 440,
  width: "100%",
  margin: "48px auto 0",
  display: "flex",
  flexWrap: "wrap",
  gap: "8px 18px",
};
const voce = {
  fontFamily: "'Be Vietnam Pro', sans-serif",
  fontSize: 13,
  color: "#8a6a78",
  textDecoration: "none",
};
